/**
 * RobinhoodSpace — cases, violations, operational summary (GovOps backend).
 */
import {
  assertGovOpsEntitlement,
  getGovOpsClient,
  isGovOpsAuthError,
  GOVOPS_ENTITLEMENT_MSG,
  fetchPermits,
  exportPermitsCsv,
} from './govopsPermitService';
import { orgQueryParams } from './govopsConfig';
import {
  formatAuditAction,
  formatStatus,
  WORKFLOW_STATE_OPTIONS,
} from '../constants/workflowLabels';

export { isGovOpsAuthError, GOVOPS_ENTITLEMENT_MSG, exportPermitsCsv };

function orgQuery() {
  return orgQueryParams();
}

export async function fetchCases() {
  await assertGovOpsEntitlement();
  const client = getGovOpsClient();
  const res = await client.listCases(orgQuery());
  return res?.cases || [];
}

export async function fetchCase(caseId) {
  await assertGovOpsEntitlement();
  const client = getGovOpsClient();
  const res = await client.getCase(caseId);
  return res?.case || null;
}

export async function fetchViolations() {
  await assertGovOpsEntitlement();
  const client = getGovOpsClient();
  const res = await client.listViolations(orgQuery());
  return res?.violations || [];
}

export async function exportCasesCsv() {
  await assertGovOpsEntitlement();
  return getGovOpsClient().exportCasesCsv(orgQuery());
}

export async function exportViolationsCsv() {
  await assertGovOpsEntitlement();
  return getGovOpsClient().exportViolationsCsv(orgQuery());
}

const IN_PROGRESS_STATUSES = new Set([
  'intake',
  'under_review',
  'notice_sent',
  'violation_open',
  'payment_pending',
  'payment_received',
  'payment_overdue',
  'renewal_due',
  'renewal_notice_sent',
  'appealed',
]);

function computeMunicipalKpisFromPermits(permits = []) {
  const now = Date.now();
  const in30d = now + 30 * 86400000;
  let unpaidPermits = 0;
  let overduePayments = 0;
  let renewalsDue = 0;
  for (const p of permits) {
    const unpaid =
      p.paymentStatus === 'unpaid' ||
      p.paymentStatus === 'overdue' ||
      (p.balanceDue !== undefined && p.balanceDue > 0);
    if (unpaid) unpaidPermits += 1;
    if (p.paymentStatus === 'overdue' || p.status === 'payment_overdue') overduePayments += 1;
    const renewalTs = p.renewalDate ? Date.parse(p.renewalDate) : NaN;
    const expTs = p.expirationDate ? Date.parse(p.expirationDate) : NaN;
    if (
      p.status === 'renewal_due' ||
      p.status === 'renewal_notice_sent' ||
      (Number.isFinite(renewalTs) && renewalTs <= in30d) ||
      (Number.isFinite(expTs) && expTs <= in30d)
    ) {
      renewalsDue += 1;
    }
  }
  return { unpaidPermits, overduePayments, renewalsDue };
}

const RESOLVED_CLOSED = new Set(['resolved', 'closed']);

function tsMs(value) {
  if (!value) return 0;
  if (value.seconds) return value.seconds * 1000;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Count records per workflow state across permits, cases, and violations. */
export function computeWorkflowStatusTotals(permits = [], cases = [], violations = []) {
  const totals = Object.fromEntries(WORKFLOW_STATE_OPTIONS.map((s) => [s, 0]));
  [...permits, ...cases, ...violations].forEach((row) => {
    const status = row.status || 'intake';
    if (totals[status] !== undefined) totals[status] += 1;
    else totals[status] = 1;
  });
  return totals;
}

function activityKindFromAudit(action = '') {
  if (action.includes('permit_created')) return 'permit';
  if (action.includes('violation_created')) return 'violation';
  if (action.includes('state_changed') || action.includes('status')) return 'status';
  if (action.includes('case')) return 'case';
  if (action.includes('permit')) return 'permit';
  if (action.includes('violation')) return 'violation';
  return 'audit';
}

/** Recent operational activity for admin dashboard (GOVOPS-1111). */
export function buildOperationalActivityFeed(
  permits = [],
  cases = [],
  violations = [],
  auditEvents = []
) {
  const events = [];

  auditEvents.forEach((ev) => {
    const from = ev.metadata?.from;
    const to = ev.metadata?.to;
    const detail =
      from && to
        ? `${from} → ${to}`
        : ev.metadata?.status || ev.entityId || '';
    events.push({
      id: ev.id,
      ts: ev.ts,
      kind: activityKindFromAudit(ev.action),
      label: formatAuditAction(ev.action),
      detail,
      entityType: ev.entityType,
      entityId: ev.entityId,
      href:
        ev.entityType === 'case'
          ? `/cases/${ev.entityId}`
          : ev.entityType === 'permit'
            ? `/permit-details/${ev.entityId}`
            : null,
    });
  });

  permits.forEach((p) => {
    events.push({
      id: `permit-create-${p.id}`,
      ts: p.createdAt || p.date,
      kind: 'permit',
      label: 'Permit created',
      detail: p.type || p.description || p.id,
      entityType: 'permit',
      entityId: p.id,
      href: `/permit-details/${p.id}`,
    });
    if (tsMs(p.updatedAt) > tsMs(p.createdAt) + 1000) {
      events.push({
        id: `permit-update-${p.id}`,
        ts: p.updatedAt,
        kind: 'status',
        label: 'Permit updated',
        detail: formatStatus(p.status),
        entityType: 'permit',
        entityId: p.id,
        href: `/permit-details/${p.id}`,
      });
    }
  });

  cases.forEach((c) => {
    events.push({
      id: `case-open-${c.id}`,
      ts: c.createdAt,
      kind: 'case',
      label: 'Case opened',
      detail: c.title || c.id,
      entityType: 'case',
      entityId: c.id,
      href: `/cases/${c.id}`,
    });
    if (tsMs(c.updatedAt) > tsMs(c.createdAt) + 1000) {
      events.push({
        id: `case-update-${c.id}`,
        ts: c.updatedAt,
        kind: 'case',
        label: 'Case updated',
        detail: formatStatus(c.status),
        entityType: 'case',
        entityId: c.id,
        href: `/cases/${c.id}`,
      });
    }
  });

  violations.forEach((v) => {
    events.push({
      id: `violation-create-${v.id}`,
      ts: v.createdAt,
      kind: 'violation',
      label: 'Violation created',
      detail: v.violationType || v.id,
      entityType: 'violation',
      entityId: v.id,
      href: v.caseId ? `/cases/${v.caseId}` : null,
    });
  });

  const seen = new Set();
  return events
    .filter((ev) => {
      const key = `${ev.id}:${ev.ts}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return tsMs(ev.ts) > 0;
    })
    .sort((a, b) => tsMs(b.ts) - tsMs(a.ts))
    .slice(0, 15);
}

/** Org-wide audit feed (optional — enriches activity table). */
export async function fetchOrgAuditEvents() {
  await assertGovOpsEntitlement();
  const client = getGovOpsClient();
  const res = await client.listAuditEvents(orgQuery());
  return res?.events || [];
}

/** KPI + lists for RobinhoodSpace admin dashboard (GOVOPS-1111). */
export async function fetchOperationalSummary() {
  const [permits, cases, violations, auditEvents] = await Promise.all([
    fetchPermits(),
    fetchCases(),
    fetchViolations(),
    fetchOrgAuditEvents().catch(() => []),
  ]);

  const recentCases = [...cases]
    .sort((a, b) => tsMs(b.updatedAt || b.createdAt) - tsMs(a.updatedAt || a.createdAt))
    .slice(0, 8);

  const appealsCount = [...permits, ...cases, ...violations].filter(
    (r) => r.status === 'appealed'
  ).length;

  const municipal = computeMunicipalKpisFromPermits(permits);

  return {
    permits,
    cases,
    violations,
    recentCases,
    workflowStatusTotals: computeWorkflowStatusTotals(permits, cases, violations),
    recentActivity: buildOperationalActivityFeed(permits, cases, violations, auditEvents),
    kpis: {
      totalPermits: permits.length,
      activePermits: permits.filter((p) => IN_PROGRESS_STATUSES.has(p.status)).length,
      resolvedClosedPermits: permits.filter((p) => RESOLVED_CLOSED.has(p.status)).length,
      violationsOpen: violations.filter((v) => v.status === 'violation_open').length,
      casesInReview: cases.filter((c) => c.status === 'under_review').length,
      appeals: appealsCount,
      totalCases: cases.length,
      totalViolations: violations.length,
      unpaidPermits: municipal.unpaidPermits,
      overduePayments: municipal.overduePayments,
      renewalsDue: municipal.renewalsDue,
    },
  };
}

/** Audit events for a case (GOVOPS-1011). */
export async function fetchCaseAuditEvents(caseId) {
  await assertGovOpsEntitlement();
  const client = getGovOpsClient();
  const res = await client.listAuditEvents({ ...orgQuery(), caseId });
  return res?.events || [];
}

/** Map GovOps audit rows to timeline events. */
export function mapAuditToTimelineEvents(auditEvents = []) {
  return auditEvents.map((ev) => {
    const from = ev.metadata?.from;
    const to = ev.metadata?.to;
    const detail =
      from && to ? `${from} → ${to}` : ev.metadata?.status || ev.action || '';
    return {
      id: ev.id,
      ts: ev.ts,
      label: formatAuditAction(ev.action),
      detail,
      kind: 'audit',
      actor: ev.ownerId,
    };
  });
}

/** Build display timeline from audit + related records. */
export function buildCaseTimeline(caseRow, permits = [], violations = [], auditEvents = []) {
  const events = [];
  if (!caseRow) return events;

  events.push({
    id: `case-created-${caseRow.id}`,
    ts: caseRow.createdAt,
    label: 'Case opened',
    detail: caseRow.title || 'False alarm permit case',
    kind: 'case',
  });

  if (caseRow.updatedAt && caseRow.updatedAt !== caseRow.createdAt) {
    events.push({
      id: `case-status-${caseRow.id}`,
      ts: caseRow.updatedAt,
      label: 'Status updated',
      detail: caseRow.status,
      kind: 'case',
    });
  }

  permits
    .filter((p) => p.caseId === caseRow.id)
    .forEach((p) => {
      events.push({
        id: `permit-${p.id}`,
        ts: p.createdAt || p.date,
        label: 'Permit recorded',
        detail: p.type || p.description || p.id,
        kind: 'permit',
      });
    });

  violations
    .filter((v) => v.caseId === caseRow.id)
    .forEach((v) => {
      events.push({
        id: `violation-${v.id}`,
        ts: v.createdAt,
        label: 'Violation',
        detail: v.violationType || v.status,
        kind: 'violation',
      });
    });

  const auditMapped = mapAuditToTimelineEvents(auditEvents);
  const merged = [...events, ...auditMapped];
  const seen = new Set();
  return merged
    .sort((a, b) => {
      const ta = a.ts ? new Date(a.ts).getTime() : 0;
      const tb = b.ts ? new Date(b.ts).getTime() : 0;
      return tb - ta;
    })
    .filter((ev) => {
      const key = `${ev.kind}:${ev.id}:${ev.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/** Case-scoped timeline export for case detail page. */
export function buildCaseTimelineCsv(caseRow, events = []) {
  const header = 'when,kind,event,detail,case_id,case_status';
  const lines = events.map((ev) => {
    const cols = [
      ev.ts || '',
      ev.kind || '',
      ev.label || '',
      ev.detail || '',
      caseRow?.id || '',
      caseRow?.status || '',
    ];
    return cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');
  });
  return [header, ...lines].join('\n');
}

export function downloadCsv(filename, csvText) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
